import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getQuizById, getQuizIdsByScenario } from "../../api/quiz";
import { postAnswer } from "../../api/answer";
import { SERVER_URL } from "../../api";
import arrowLeft from "../../assets/arrow-left.png";
import arrowRight from "../../assets/arrow-right.png";
import buttonAmpliar from "../../assets/button-ampliar.png";
import ScenarioBar from "../../components/ScenarioBar";
import { useTimer } from "../../hooks/useTimer";
import { updateScore, updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import {
	getScenarioByScreen,
	getTimer,
	updateStats,
} from "../../utils/helpers";
import {
	alertError,
	modalGenericSmall,
	modalImage,
	modalQuestion3Feedback,
} from "../../utils/logger";

const QUIZ_ID = QUIZZES.SCENARIO3;
const SCENARIO_ID = SCENARIOS.SCENARIO_3;
const NAVIGATE_TO = ROUTES.SCENARIO3_EXPLANATION;

const CORRECT_ANSWER_POINTS = 40;

function buildImageUrl(value) {
	if (!value || typeof value !== "string") return null;
	if (value.startsWith("http")) return value;
	return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

/** Fisher-Yates: baraja el array y devuelve una nueva instancia (orden aleatorio en cada llamada). */
function shuffleArray(arr) {
	const a = arr.slice();
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

/**
 * Baraja por separado la lista de opciones y la lista de imágenes,
 * para que la 1ª opción no corresponda a la 1ª imagen, etc.
 * Cada opción conserva su correctImage; solo cambia el orden en que se muestran.
 */
function shuffleOptionsAndImages(optionsWithCorrectImage, images) {
	if (!optionsWithCorrectImage?.length || !images?.length) {
		return { options: optionsWithCorrectImage || [], images: images || [] };
	}
	const options = shuffleArray(optionsWithCorrectImage).map((o) => ({
		...o,
		selectedImage: [false, false, false, false],
		checked: false,
	}));
	const imagesShuffled = shuffleArray(images);
	return { options, images: imagesShuffled };
}

function Scenario3({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const participant = useSelector((state) => state?.group);
	const cachedQuiz = useSelector((state) => state.quizCache?.[SCENARIO_ID]);

	const scenarioNum = getScenarioByScreen(location.pathname);

	const [time, setTime] = useState(0);
	const [isCompleted, setIsCompleted] = useState(false);
	const [options, setOptions] = useState([]);
	const [images, setImages] = useState([]);
	const [activeImage, setActiveImage] = useState(null);
	const [resolvedQuizId, setResolvedQuizId] = useState(null);
	const [quizLoading, setQuizLoading] = useState(true);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) {
			setQuizLoading(false);
			return;
		}

		// Usar caché si se precargó desde la pantalla anterior
		if (cachedQuiz?.data?.options?.length >= 4) {
			const opts = cachedQuiz.data.options.slice().sort((a, b) => (a.id || 0) - (b.id || 0));
			const imgs = opts.map((o) => buildImageUrl(o.image) || null);
			const built = opts.map((o, i) => ({
				id: o.id,
				text: o.text || "",
				correctImage: imgs[i],
				selectedImage: [false, false, false, false],
				checked: false,
			}));
			const shuffled = shuffleOptionsAndImages(built, imgs);
			setResolvedQuizId(cachedQuiz.resolvedQuizId || null);
			setImages(shuffled.images);
			setActiveImage(shuffled.images[0]);
			setOptions(shuffled.options);
			setQuizLoading(false);
			return;
		}

		setQuizLoading(true);
		let cancelled = false;
		const params = { game: gameId };
		getQuizIdsByScenario(SCENARIO_ID, params)
			.then((listRes) => {
				if (cancelled) return;
				const list = listRes?.data && Array.isArray(listRes.data) ? listRes.data : [];
				const id = list[0]?._id || QUIZ_ID;
				setResolvedQuizId(id);
				return getQuizById(id);
			})
			.then((res) => {
				if (cancelled) return;
				if (res?.status === 200 && res.data?.options?.length >= 4) {
					const opts = res.data.options.slice().sort((a, b) => (a.id || 0) - (b.id || 0));
					const imgs = opts.map((o) => buildImageUrl(o.image) || null);
					const built = opts.map((o, i) => ({
						id: o.id,
						text: o.text || "",
						correctImage: imgs[i],
						selectedImage: [false, false, false, false],
						checked: false,
					}));
					const shuffled = shuffleOptionsAndImages(built, imgs);
					setImages(shuffled.images);
					setActiveImage(shuffled.images[0]);
					setOptions(shuffled.options);
				}
				setQuizLoading(false);
			})
			.catch(() => { if (!cancelled) setQuizLoading(false); });
		return () => { cancelled = true; };
	}, [participant?.game]);

	useTimer(
		() => {
			setTime(time + 1);
		},
		isCompleted ? null : 1000
	);

	const activeImageIndex = images.length ? images.findIndex((x) => x === activeImage) : -1;

	const handleOptionClick = (id) => {
		if (isCompleted) return;
		const index = activeImageIndex;
		let updatedOptions = options.map((x) => {
			if (x.id === id) {
				let updatedSelected = [...x.selectedImage];
				const current = x.selectedImage[index];
				if (current) updatedSelected[index] = false;
				else updatedSelected[index] = images[index];
				return { ...x, selectedImage: updatedSelected };
			}
			let updatedSelected = [...x.selectedImage];
			updatedSelected[index] = false;
			return { ...x, selectedImage: updatedSelected };
		});
		setOptions(updatedOptions);
	};

	const goToNextImage = () => {
		const nextIndex = (activeImageIndex + 1) % images.length;
		setActiveImage(images[nextIndex]);
	};

	const goToPrevImage = () => {
		const prevIndex = (activeImageIndex - 1 + images.length) % images.length;
		setActiveImage(images[prevIndex]);
	};

	function checkAnswers() {
		function setCurrentOptionAsChecked() {
			const updatedOptions = options.map((option) => {
				if (!option.selectedImage?.includes(option.correctImage)) {
					return {
						...option,
						selectedImage: [false, false, false, false],
					};
				} else return { ...option, checked: true };
			});
			setOptions(updatedOptions);
		}

		const activeIndex = images.findIndex((x) => x === activeImage);

		if (options?.every((x) => !x.selectedImage[activeIndex])) {
			alertError({
				error: " ",
				customMessage: "Para avanzar debes seleccionar una opción.",
			});
			return;
		} else if (
			!options?.some(
				(x) => x.selectedImage[activeIndex] === x.correctImage
			)
		) {
			const text = `<div class="text-center">
			<p class="txt-turquoise bold">La etiqueta es incorrecta.</p>
			<p class="txt-black">Inténtalo de nuevo</p>
		</div>`;

			modalGenericSmall({
				html: text,
				confirmButtonText: "INTENTAR DE NUEVO",
				allowOutsideClick: true,
			});
		} else if (activeIndex < images.length - 1) {
			const text = `<div class="text-center">
					<p class="txt-turquoise bold">
						¡Correcto!
					</p>
					<p class="txt-black">
						¡A por la siguiente!
					</p>
				</div>`;

			modalGenericSmall({
				html: text,
				confirmButtonText: "AVANZAR",
				allowOutsideClick: false,
			}).then((res) => {
				if (res.isConfirmed) {
					setActiveImage(
						images[
							(images.indexOf(activeImage) + 1) % images.length
						]
					);

					setCurrentOptionAsChecked();
				}
			});
		} else {
			const text = `<div class="text-center">
					<p class="txt-turquoise bold">
						¡Excelente, todo está correcto!
					</p>
					<p class="txt-black">
						Echa un vistazo a la solución y ya podrás responder al
						correo de Ricardo indicándole qué muestra cada imagen.
						Por el hecho de superar esta prueba, has ganado:
					</p>
				</div>`;

			modalQuestion3Feedback({
				text,
				correct: true,
				points: CORRECT_ANSWER_POINTS,
			}).then((res) => {
				if (res.isConfirmed) {
					setCurrentOptionAsChecked();
					submitAnswer();
				}
			});
		}
	}

	async function submitAnswer() {
		setIsCompleted(true);

		let totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: resolvedQuizId ?? QUIZ_ID,
			group: participant?._id,
			score: CORRECT_ANSWER_POINTS,
			time: time,
			correct: true,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(
						updateScore(CORRECT_ANSWER_POINTS, SCORE_TYPES.SCORE)
					);

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + CORRECT_ANSWER_POINTS,
						lastVisited: NAVIGATE_TO,
					});
				}
			})
			.catch((error) => {});
	}

	if (quizLoading) {
		return (
			<div className="h-100 bg-prueba d-flex align-items-center justify-content-center">
				<div className="txtWhite">Cargando quiz...</div>
				<ScenarioBar activeScenario={scenarioNum} />
			</div>
		);
	}

	if (!options?.length || !images?.length) {
		return (
			<div className="h-100 bg-prueba d-flex align-items-center justify-content-center">
				<div className="txtWhite">No hay opciones de quiz configuradas.</div>
				<ScenarioBar activeScenario={scenarioNum} />
			</div>
		);
	}

	return (
		<div className="h-100 bg-prueba">
			<div className="container">
				<div className="row h-100">
					<div className="row d-flex justify-content-center align-items-center my-4">
						<div className="col-8 px-4">
							<div className="d-flex align-items-center">
								{isCompleted && <img onClick={goToPrevImage} src={arrowLeft} alt="" />}
								<div className="image-carousel-container d-flex justify-content-center w-100 mx-3">
									{activeImage ? (
										<img height={400} src={activeImage} alt="" />
									) : (
										<div style={{ width: "100%", height: 400, background: "#e0e0e0", borderRadius: 8 }} aria-hidden />
									)}
									{activeImage && (
										<div
											style={{
												position: "absolute",
												bottom: "5%",
												right: "5%",
											}}
										>
											<img
												onClick={() => modalImage({ image: activeImage })}
												width={70}
												src={buttonAmpliar}
												alt=""
											/>
										</div>
									)}
								</div>
								{isCompleted && <img onClick={goToNextImage} src={arrowRight} alt="" />}
							</div>
						</div>
						<div className="col-4">
							{options.map((option) => {
								const selectedImage = option?.selectedImage[activeImageIndex];
								const backgroundColor =
									selectedImage && option?.correctImage === selectedImage && option?.checked
										? "#D6D431"
										: selectedImage
										? "#EAF886"
										: "#FFFFFF";
								return (
									<div key={option.id}>
										<div
											className={`d-flex flex-column align-items-center justify-content-center txt-turquoise text-center mt-3 p-2 ${
												selectedImage ? "option-selected" : ""
											}`}
											style={{
												height: "8vh",
												borderRadius: "10px",
												backgroundColor,
											}}
											onClick={() => handleOptionClick(option.id)}
										>
											<div className="bold">{option?.text}</div>
											{option?.content}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					bottom: "13vh",
					zIndex: 1000,
				}}
			>
				<div className="d-flex justify-content-end">
					<div
						className="btn-main"
						onClick={() => {
							if (!isCompleted) checkAnswers();
							else
								navigate(NAVIGATE_TO, {
									state: { activeScenario: scenarioNum + 1 },
								});
						}}
					>
						<div className="text-btn-main">AVANZAR</div>
					</div>
				</div>
			</div>
			<ScenarioBar activeScenario={scenarioNum}></ScenarioBar>
		</div>
	);
}

export default injectIntl(Scenario3);
